import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel, Send } from '@mui/icons-material';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import EmailTemplateIcon from '@mui/icons-material/ViewCompactOutlined';
import { Box, Chip, Grid, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { object } from 'yup';

import EmailFooter from '../../components/EmailFooter';
import EmailTextBorder from '../../components/EmailTextBorder';
import Layout from '../../components/Layout/Layout';
import WsButton from '../../components/WsButton/WsButton';
import { useWsEditor, WsEditor } from '../../components/WsEditor';
import { WsTypoColors, WsTypography } from '../../components/WsTypography';

import EmailInput from './components/EmailInput';
import FormAttachment from './components/FormAttachment';
import InvalidEmailData from './components/InvalidEmailDataDrawer';
import { EmailFormProps, FormLabel, ReceiverInput, SubjectInput, validationSchema } from './helper';

const EmailForm = ({
  fromEmailList = [],
  toEmailList = [],
  template = '',
  subjectTemplate = '',
  templateName,
  resizable = true,
  fromEmailSignature = '',
  disableSend = false,
  updateFromEmailSignature,
  openTemplatesList,
  manageSenderAccounts,
  uploadAttachment,
  handleAttachmentError,
  submitAction,
  handleClose,
  ccList = [],
  bccList = [],
  isDirtyCallback,
  deleteAttachmentHandler,
}: EmailFormProps) => {
  const noEmail = isEmpty(toEmailList[0]);
  const EmailDefaultValues = {
    to: toEmailList,
    cc: ccList,
    bcc: bccList,
    from: fromEmailList[0],
    subject: isEmpty(subjectTemplate) ? '' : subjectTemplate,
    body: isEmpty(template) ? '' : template,
  };
  const formOptions = {
    resolver: yupResolver(object().shape(validationSchema)),
    defaultValues: EmailDefaultValues,
  };
  const methods = useForm(formOptions);
  const {
    handleSubmit,
    setValue,
    control,
    formState: { isDirty, isSubmitSuccessful },
  } = methods;

  const [openInvalidWarning, setOpenInvalidWarning] = useState(false);

  type singleBooleanStateType = {
    openCC: boolean;
    openBCC: boolean;
  };
  const [singleBooleanState, setSingleBooleanState] = useState<singleBooleanStateType>({
    openBCC: false,
    openCC: false,
  });
  const [fileNames, setFileNames] = useState<(string | null)[]>([]);

  const handleAttachments = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0].size < 25000000) {
      event.target.files && uploadAttachment(event.target.files[0]);
      setFileNames((prev) => [...prev, event.target.files && event.target.files[0].name]);
    } else handleAttachmentError();
  };

  const onDeleteAttachment = (selectedFileName: string) => {
    const tempData: (string | null)[] = fileNames.filter((name) => selectedFileName !== name);
    setFileNames([...tempData]);
    deleteAttachmentHandler && deleteAttachmentHandler(selectedFileName);
  };
  const editor = useWsEditor(EmailDefaultValues.body ?? '', (htmlBody) =>
    setValue('body', htmlBody)
  );

  const deleteTemplate = () => {
    editor?.commands.setContent('');
    methods.setValue('body', '');
    methods.setValue('subject', '');
  };
  const SenderInput = () => (
    <Grid xs={6}>
      <Controller
        name="from"
        control={control}
        render={({ field }) => (
          <Select
            fullWidth
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              updateFromEmailSignature && updateFromEmailSignature(value.target.value);
            }}
            variant="standard"
            disableUnderline
            label={''}
          >
            {fromEmailList.map((email, i) => (
              <MenuItem value={email} key={i}>
                {email}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </Grid>
  );
  useEffect(() => {
    if (!isSubmitSuccessful && isDirty) {
      isDirtyCallback(!isSubmitSuccessful);
    }
  }, [isDirty, isDirtyCallback, isSubmitSuccessful]);
  const submitForm = (data: FieldValues) => {
    data.body = `${data.body}\n\n\n ${fromEmailSignature}`;
    submitAction(data);
  };
  return (
    <Layout>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <Box component="form">
            <Grid container sx={{ height: '90vh', overflowY: 'auto' }}>
              <Grid item xs={12}>
                <EmailTextBorder>
                  <Stack direction="row" alignItems={'center'}>
                    <FormLabel label="From" />
                    <SenderInput />
                    <Grid item xs={4}>
                      <Stack direction="row" justifyContent={'flex-end'}>
                        <WsButton
                          variant="text"
                          startIcon={<BorderColorOutlinedIcon />}
                          onClick={manageSenderAccounts}
                        >
                          Add/Manage Accounts
                        </WsButton>
                      </Stack>
                    </Grid>
                  </Stack>
                </EmailTextBorder>
                <EmailTextBorder>
                  <Stack direction="row" alignItems={'center'}>
                    <FormLabel label="To" />
                    <ReceiverInput name={'to'} />
                    <Grid item xs={3}>
                      <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
                        <WsButton
                          variant="text"
                          onClick={() =>
                            setSingleBooleanState({
                              openCC: !singleBooleanState.openCC,
                              openBCC: singleBooleanState.openBCC,
                            })
                          }
                        >
                          Cc
                        </WsButton>
                        <WsButton
                          variant="text"
                          onClick={() =>
                            setSingleBooleanState({
                              openCC: singleBooleanState.openCC,
                              openBCC: !singleBooleanState.openBCC,
                            })
                          }
                        >
                          Bcc
                        </WsButton>
                      </Stack>
                    </Grid>
                  </Stack>
                </EmailTextBorder>
                {singleBooleanState.openCC && (
                  <EmailInput
                    name={'Cc'}
                    nameForReceiverInput={'cc'}
                    callbackHandler={() =>
                      setSingleBooleanState({
                        openCC: false,
                        openBCC: singleBooleanState.openBCC,
                      })
                    }
                  />
                )}
                {singleBooleanState.openBCC && (
                  <EmailInput
                    name={'Bcc'}
                    nameForReceiverInput={'bcc'}
                    callbackHandler={() =>
                      setSingleBooleanState({
                        openCC: singleBooleanState.openBCC,
                        openBCC: false,
                      })
                    }
                  />
                )}
                <EmailTextBorder>
                  <Stack direction="row" alignItems={'center'}>
                    <FormLabel label="Subject" />
                    <SubjectInput />
                  </Stack>
                </EmailTextBorder>
                <WsEditor
                  key="body"
                  templateName={templateName ?? ''}
                  editor={editor}
                  resizable={resizable}
                  handleChipClick={openTemplatesList}
                  handleChipDelete={deleteTemplate}
                  editorHeight={400}
                />
                <div dangerouslySetInnerHTML={{ __html: fromEmailSignature }}></div>
                {methods.formState.errors.body && (
                  <WsTypography color={WsTypoColors.ERROR}>The body is empty!</WsTypography>
                )}
              </Grid>
            </Grid>
            <EmailFooter item xs={12}>
              {fileNames.length > 0 && (
                <Stack direction="row" width={'fit-content'} spacing={1} p={0.5}>
                  {fileNames.map((fileName: string, key: number) => (
                    <Chip
                      key={key}
                      label={fileName}
                      onDelete={() => onDeleteAttachment(fileName)}
                    />
                  ))}
                </Stack>
              )}
              <Stack direction="row" justifyContent={'space-between'} alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Tooltip title={'Coming Soon'}>
                    <WsButton
                      variant="text"
                      startIcon={<EmailTemplateIcon />}
                      onClick={openTemplatesList}
                      disabled={true}
                    >
                      Select Template
                    </WsButton>
                  </Tooltip>
                  <FormAttachment handleAttachments={(event) => handleAttachments(event)} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <WsButton
                    size="large"
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleClose}
                  >
                    Cancel
                  </WsButton>
                  <WsButton
                    size="large"
                    startIcon={<Send />}
                    disabled={disableSend}
                    onClick={handleSubmit((data) => submitForm(data))}
                  >
                    Send
                  </WsButton>
                </Stack>
              </Stack>
            </EmailFooter>
          </Box>
        </FormProvider>
      </Grid>
      <InvalidEmailData
        openInvalidWarning={openInvalidWarning}
        noEmail={noEmail}
        onCloseHandler={() => setOpenInvalidWarning(false)}
      />
    </Layout>
  );
};

export default EmailForm;
